
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,

  Typography,
  Button,
  IconButton,
  Stack,
 
} from "@mui/material";
import { ArrowBack, ChevronLeft, ChevronRight, Send } from "@mui/icons-material";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import { API_URL } from "../config";
import TestSuccessAlertPage from "../common/alert/TestSuccessAlertPage";
import TestFailedAlertPage from "../common/alert/TestFailedAlertPage";
import { toast } from "react-toastify";

const QuestionsPage = () => {
  const navigate = useNavigate();
  const { examId, subjectId, testId } = useParams();

  const [questions, setQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [resultData, setResultData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const tokenString = localStorage.getItem("authToken");
        if (!tokenString) throw new Error("No token");

        const token = JSON.parse(tokenString);
        const res = await fetch(
          `${API_URL}/exams/${examId}/subjects/${subjectId}/tests/${testId}/questions`,
          {
            headers: { Authorization: `Bearer ${token.access}` },
          }
        );

        if (!res.ok) throw new Error("Failed to load");

        const data = await res.json();
        setQuestions(data);
        console.log(data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [examId, subjectId, testId]);

  const handleOptionClick = (optionId) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [currentQuestion.id]: optionId,
    }));
  };

  const handleSubmit = async () => {
    try {
      const tokenString = localStorage.getItem("authToken");
      if (!tokenString) throw new Error("No token");

      const token = JSON.parse(tokenString);

      const res = await fetch(
        `${API_URL}/submit-answers/${examId}/${subjectId}/${testId}/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ answers: selectedOptions }),
        }
      );

      const data = await res.json();
      console.log("Result Data:", data);

      setResultData(data);

      const failedCount = data.total_questions - data.correct_count;

      if (failedCount > 0) {
        setShowFailed(true); // ❌ Student failed
      } else {
        setShowSuccess(true); // ✅ Student passed
      }
    } catch (err) {
      console.error("Submit Error:", err);
      toast.error("Submission failed");
    }
  };

  const failedResults = Array.isArray(resultData.results)
    ? resultData.results.filter((r) => r.result === "incorrect")
    : [];

  console.log(failedResults, resultData.results);

  const currentQuestion = questions[currentIndex];

  const renderMath = (text) => {
    if (!text) return null;

    const parts = text.split(
      /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g
    );

    return parts.map((part, index) => {
      // $$...$$ (block)
      if (part.startsWith("$$") && part.endsWith("$$")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \[...\] (block)
      if (part.startsWith("\\[") && part.endsWith("\\]")) {
        return (
          <div key={index} style={{ margin: "10px 0" }}>
            <BlockMath math={part.slice(2, -2)} />
          </div>
        );
      }

      // \(...\) (inline)
      if (part.startsWith("\\(") && part.endsWith("\\)")) {
        return (
          <InlineMath key={index} math={part.slice(2, -2)} />
        );
      }

      // $...$ (inline)
      if (part.startsWith("$") && part.endsWith("$")) {
        return (
          <InlineMath key={index} math={part.slice(1, -1)} />
        );
      }

      // Normal text
      return (
        <span key={index} style={{ whiteSpace: "pre-line" }}>
          {part}
        </span>
      );
    });
  };

  // ─── LOADING STATE ───────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f8f7f4",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: "2.5px solid rgba(168,85,247,0.15)",
            borderTopColor: "#a855f7",
            borderRightColor: "rgba(99,60,180,0.5)",
            animation: "spin 0.9s linear infinite",
            "@keyframes spin": { to: { transform: "rotate(360deg)" } },
          }}
        />
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.7rem",
            letterSpacing: "0.16em",
            color: "rgba(99,60,180,0.5)",
            textTransform: "uppercase",
          }}
        >
          Loading questions
        </Typography>
        <Stack direction="row" spacing={0.7}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#633cb4,#a855f7)",
                animation: "dotBeat 1.4s ease-in-out infinite",
                animationDelay: `${i * 0.2}s`,
                "@keyframes dotBeat": {
                  "0%,80%,100%": { transform: "scale(0.5)", opacity: 0.3 },
                  "40%": { transform: "scale(1.1)", opacity: 1 },
                },
              }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // ─── COMING SOON ─────────────────────────────────────────────
  if (questions.length === 0) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f8f7f4",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          backgroundImage:
            "linear-gradient(rgba(99,60,180,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(99,60,180,0.04) 1px,transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      >
        <Typography sx={{ fontSize: "2.5rem" }}>🔭</Typography>
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "1rem",
            letterSpacing: "0.22em",
            color: "#633cb4",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          Coming Soon
        </Typography>
        <Typography sx={{ fontSize: "0.82rem", color: "rgba(26,26,46,0.4)" }}>
          Questions are being prepared
        </Typography>
      </Box>
    );
  }

 // ─── MAIN QUIZ ────────────────────────────────────────────────
return (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: "#f8f7f4",
      fontFamily: "'Sora',sans-serif",
      color: "#1a1a2e",
      backgroundImage:
        "linear-gradient(rgba(99,60,180,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(99,60,180,0.04) 1px,transparent 1px)",
      backgroundSize: "44px 44px",
    }}
  >
    {/* Animated rainbow accent bar at very top */}
    <Box
      sx={{
        height: 3,
        background:
          "linear-gradient(90deg,#633cb4,#a855f7,#e879f9,#f97316,#633cb4)",
        backgroundSize: "300% 100%",
        animation: "gradientSlide 4s linear infinite",
        "@keyframes gradientSlide": { to: { backgroundPosition: "-300% 0" } },
      }}
    />

    {/* HEADER */}
    <Box
      sx={{
        px: { xs: 2.5, md: 4 },
        pt: 2.5,
        pb: 0,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          border: "1.5px solid rgba(99,60,180,0.2)",
          bgcolor: "rgba(99,60,180,0.06)",
          color: "#633cb4",
          "&:hover": {
            bgcolor: "rgba(99,60,180,0.12)",
            borderColor: "rgba(99,60,180,0.4)",
          },
        }}
      >
        <ArrowBack fontSize="small" />
      </IconButton>

      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.67rem",
            letterSpacing: "0.16em",
            color: "#633cb4",
            textTransform: "uppercase",
            fontWeight: 500,
            mb: 0.8,
          }}
        >
          Question {currentIndex + 1} of {questions.length}
        </Typography>

        <Box
          sx={{
            height: 5,
            bgcolor: "rgba(99,60,180,0.1)",
            borderRadius: 99,
            overflow: "visible",
            position: "relative",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${((currentIndex + 1) / questions.length) * 100}%`,
              borderRadius: 99,
              background: "linear-gradient(90deg,#633cb4,#a855f7,#e879f9)",
              boxShadow: "0 0 10px rgba(168,85,247,0.4)",
              transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                right: -1,
                top: "50%",
                transform: "translateY(-50%)",
                width: 9,
                height: 9,
                borderRadius: "50%",
                bgcolor: "#a855f7",
                boxShadow: "0 0 8px rgba(168,85,247,0.7)",
                border: "2px solid #fff",
              },
            }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.68rem",
            color: "rgba(26,26,46,0.35)",
            mt: 0.6,
          }}
        >
          {String(currentIndex + 1).padStart(2, "0")} /{" "}
          {String(questions.length).padStart(2, "0")}
        </Typography>
      </Box>
    </Box>

    {/* BODY */}
    <Container maxWidth="md" sx={{ py: 3.5 }}>
      {currentQuestion?.q_img && (
        <Box textAlign="center" mb={3}>
          <Box
            component="img"
            src={currentQuestion.q_img}
            alt="Question"
            sx={{
              maxWidth: "100%",
              borderRadius: 3,
              border: "1.5px solid rgba(99,60,180,0.1)",
            }}
          />
        </Box>
      )}

      {/* Question box */}
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 2.5,
          bgcolor: "#fff",
          border: "1.5px solid rgba(99,60,180,0.12)",
          borderLeft: "4px solid #a855f7",
          borderRadius: "14px",
          boxShadow: "0 2px 16px rgba(99,60,180,0.07)",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "1rem", md: "1.05rem" },
            fontWeight: 600,
            lineHeight: 1.75,
            color: "#1a1a2e",
          }}
        >
          {renderMath(currentQuestion?.text)}
        </Typography>
      </Box>

      {/* Options */}
      <Stack spacing={1.25}>
        {currentQuestion?.options?.map((option) => {
          const isSelected = selectedOptions[currentQuestion.id] === option.id;
          return (
            <Box
              key={option.id}
              onClick={() => handleOptionClick(option.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                p: { xs: 1.75, md: 2 },
                bgcolor: isSelected ? "rgba(168,85,247,0.05)" : "#fff",
                border: "1.5px solid",
                borderColor: isSelected ? "#a855f7" : "rgba(26,26,46,0.1)",
                borderRadius: "12px",
                cursor: "pointer",
                transform: isSelected ? "translateX(6px)" : "translateX(0)",
                boxShadow: isSelected
                  ? "0 6px 24px rgba(168,85,247,0.15)"
                  : "0 1px 4px rgba(0,0,0,0.04)",
                transition: "all 0.22s cubic-bezier(0.4,0,0.2,1)",
                "&:hover": {
                  borderColor: isSelected ? "#a855f7" : "rgba(99,60,180,0.3)",
                  transform: "translateX(4px)",
                  boxShadow: "0 4px 16px rgba(99,60,180,0.1)",
                },
              }}
            >
              {/* Letter */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "9px",
                  flexShrink: 0,
                  border: "1.5px solid",
                  transition: "all 0.22s",
                  borderColor: isSelected
                    ? "transparent"
                    : "rgba(26,26,46,0.12)",
                  background: isSelected
                    ? "linear-gradient(135deg,#633cb4,#a855f7)"
                    : "#f8f7f4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: isSelected ? "#fff" : "rgba(26,26,46,0.45)",
                  boxShadow: isSelected
                    ? "0 4px 14px rgba(168,85,247,0.45)"
                    : "none",
                }}
              >
                {option.option_letter}
              </Box>

              <Typography
                sx={{
                  flex: 1,
                  fontSize: { xs: "0.88rem", md: "0.92rem" },
                  lineHeight: 1.6,
                  transition: "all 0.22s",
                  color: isSelected ? "#1a1a2e" : "rgba(26,26,46,0.62)",
                  fontWeight: isSelected ? 500 : 400,
                }}
              >
                {renderMath(option?.text)}
              </Typography>

              {option?.option_img && (
                <Box
                  component="img"
                  src={option.option_img}
                  alt="Option"
                  sx={{
                    maxHeight: 80,
                    borderRadius: 2,
                    border: "1.5px solid rgba(99,60,180,0.1)",
                  }}
                />
              )}

              {/* Check */}
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  flexShrink: 0,
                  border: "1.5px solid",
                  transition: "all 0.22s",
                  borderColor: isSelected
                    ? "transparent"
                    : "rgba(26,26,46,0.15)",
                  background: isSelected
                    ? "linear-gradient(135deg,#633cb4,#a855f7)"
                    : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  fontWeight: 700,
                  color: isSelected ? "#fff" : "transparent",
                  boxShadow: isSelected
                    ? "0 2px 10px rgba(168,85,247,0.4)"
                    : "none",
                }}
              >
                ✓
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* FOOTER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 3,
          mt: 3.5,
          borderTop: "1px solid rgba(26,26,46,0.07)",
          gap: 2,
        }}
      >
        <Button
          onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
          disabled={currentIndex === 0}
          startIcon={<ChevronLeft />}
          sx={{
            fontFamily: "'Sora',sans-serif",
            fontSize: "0.82rem",
            fontWeight: 500,
            color: "rgba(26,26,46,0.5)",
            border: "1.5px solid rgba(26,26,46,0.12)",
            borderRadius: "10px",
            px: 2.5,
            py: 1.1,
            bgcolor: "#fff",
            minWidth: 100,
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            "&:hover": {
              borderColor: "rgba(99,60,180,0.3)",
              color: "#633cb4",
              boxShadow: "0 4px 12px rgba(99,60,180,0.1)",
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": { opacity: 0.25 },
          }}
        >
          Prev
        </Button>

        <Typography
          sx={{
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.75rem",
            color: "rgba(26,26,46,0.3)",
            flex: 1,
            textAlign: "center",
          }}
        >
          {Object.keys(selectedOptions).length} answered
        </Typography>

        {currentIndex === questions.length - 1 ? (
          <Button
            onClick={handleSubmit}
            endIcon={<Send />}
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "0.82rem",
              fontWeight: 600,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              px: 2.5,
              py: 1.1,
              background: "linear-gradient(135deg,#f97316,#fb923c)",
              boxShadow: "0 4px 18px rgba(249,115,22,0.3)",
              minWidth: 100,
              "&:hover": {
                boxShadow: "0 6px 28px rgba(249,115,22,0.45)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Submit
          </Button>
        ) : (
          <Button
            onClick={() => setCurrentIndex(currentIndex + 1)}
            endIcon={<ChevronRight />}
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontSize: "0.82rem",
              fontWeight: 600,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              px: 2.5,
              py: 1.1,
              background: "linear-gradient(135deg,#633cb4,#a855f7)",
              boxShadow: "0 4px 18px rgba(168,85,247,0.3)",
              minWidth: 100,
              "&:hover": {
                boxShadow: "0 6px 28px rgba(168,85,247,0.45)",
                transform: "translateY(-2px)",
              },
            }}
          >
            Next
          </Button>
        )}
      </Box>
    </Container>

    {/* Alerts */}
    <TestSuccessAlertPage
      visible={showSuccess}
      title="You passed!"
      message={resultData.message}
      onClose={() => navigate(`/tests/${examId}/${subjectId}`)}
    />

    <TestFailedAlertPage
      visible={showFailed}
      title="Oops!"
      message={resultData.message}
      failedQuestionNumber={failedResults.map((r) => r.question_number)}
      failedQuestionText={failedResults.map((r) => r.question_text)}
      correctAnswer={failedResults.map((r) => r.correct_options)}
      failedAnswer={failedResults.map((r) => r.selected_options)}
      solutions={failedResults.map((r) => r.solution)}
      onClose={() => {
        setShowFailed(false);
        setSelectedOptions({});
        setCurrentIndex(0);
      }}
      questions={questions}
    />
  </Box>
);
};

export default QuestionsPage;






